/**
 * Summaries and alerts (SPEC §6.6).
 *
 * A summary is what a parent gets instead of a conversation. The screen states
 * that at the top rather than in a settings page — a parent who does not know
 * what they are *not* seeing cannot judge whether it is fair.
 */

import { useEffect, useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import { Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { useParams } from "react-router-dom";

type Summary = { date?: string; title?: string; text?: string; level?: string };

const readSummary = (raw: Record<string, unknown>): Summary => ({
  date: typeof raw.date === "string" ? raw.date : undefined,
  title: typeof raw.title === "string" ? raw.title : undefined,
  text: typeof raw.text === "string" ? raw.text : typeof raw.summary === "string" ? raw.summary : undefined,
  level: typeof raw.level === "string" ? raw.level : undefined,
});

export function SummariesScreen() {
  const { childId = "" } = useParams();
  const [summaries, setSummaries] = useState<Summary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await call("summaries", { params: { child_id: childId }, query: { limit: 30 } });
        if (!cancelled) setSummaries((loaded.summaries ?? []).map(readSummary));
      } catch (failure) {
        if (!cancelled) setError(failure instanceof ApiError ? failure.message : "Chargement impossible.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [childId]);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!summaries) return <Loading />;

  return (
    <Screen title="Résumés de séance">
      <p className="notice">
        Ce sont des résumés écrits par le tuteur. Le verbatim des séances ne remonte pas ici —
        sauf alerte urgente, et vous en seriez prévenus autrement.
      </p>
      {summaries.length === 0 ? (
        <Card>
          <Empty>Aucun résumé de séance pour l'instant.</Empty>
        </Card>
      ) : (
        summaries.map((summary, index) => (
          <Card key={index} title={summary.title ?? summary.date ?? "Compte rendu"} tone={summary.level === "urgent" ? "attention" : "neutral"}>
            {summary.date ? <p className="muted small">{summary.date}</p> : null}
            <p>{summary.text ?? "—"}</p>
          </Card>
        ))
      )}
    </Screen>
  );
}
